import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { MENU_CATEGORIES, MENU_ITEMS } from "../data/content";

export default function Menu() {
  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");

  const items = useMemo(() => {
    return MENU_ITEMS.filter((m) => {
      const matchCat = active === "All" || m.cat === active;
      const matchQ = query.trim() === "" || m.name.toLowerCase().includes(query.toLowerCase());
      return matchCat && matchQ;
    });
  }, [active, query]);

  return (
    <div data-testid="menu-page" className="pt-28">
      {/* HERO */}
      <section className="py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
          <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Our full menu</span>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1]">
            A millet café menu <br /> people actually <em className="italic text-[#C76D4B]">crave.</em>
          </h1>
          <p className="mt-8 text-[#7A5C45] max-w-2xl mx-auto">
            No maida. No preservatives. Just thoughtful plates and warm cups built around India's
            most underrated grain.
          </p>
        </div>
      </section>

      {/* CONTROLS */}
      <section className="px-5 sm:px-8">
        <div className="max-w-7xl mx-auto bg-[#F3EBE1] rounded-3xl p-5 sm:p-6 flex flex-col gap-4">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A5C45]" />
            <input
              data-testid="menu-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the menu… try 'risotto' or 'ragi'"
              className="w-full bg-white rounded-full pl-11 pr-4 py-3 text-sm border border-[#E8D8C8] focus:outline-none focus:border-[#C76D4B]"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {MENU_CATEGORIES.map((c) => {
              const isActive = active === c;
              return (
                <button
                  key={c}
                  data-testid={`menu-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setActive(c)}
                  className={`whitespace-nowrap rounded-full px-5 py-2 text-sm transition border ${
                    isActive
                      ? "bg-[#2A1E17] text-[#FAF7F2] border-[#2A1E17]"
                      : "bg-white text-[#4A3320] border-[#E8D8C8] hover:border-[#C76D4B]"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {items.length === 0 ? (
            <div className="text-center py-20 text-[#7A5C45]" data-testid="menu-empty">
              No dishes match that search. Try another grain.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
              {items.map((m, i) => (
                <article
                  key={m.name}
                  data-testid={`menu-item-${i}`}
                  className="card-lift bg-white rounded-3xl overflow-hidden border border-[#E8D8C8] flex flex-col"
                >
                  <div className="relative overflow-hidden">
                    <img src={m.img} alt={m.name} className="w-full h-60 object-cover img-zoom" />
                    <span className="absolute top-4 left-4 bg-[#FAF7F2]/95 backdrop-blur text-[10px] tracking-[0.22em] uppercase text-[#C76D4B] px-3 py-1 rounded-full">
                      {m.tag}
                    </span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#7A5C45]">
                      {m.cat}
                    </div>
                    <h3 className="font-serif-display text-2xl mt-2 leading-tight">{m.name}</h3>
                    <p className="text-sm text-[#7A5C45] mt-3 flex-1">{m.desc}</p>
                    <div className="mt-5 flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase text-[#7B8B6F]">
                      <span>Millet Made</span>
                      <span className="text-[#E8D8C8]">·</span>
                      <span>Made Fresh</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="px-5 sm:px-8">
        <div className="max-w-7xl mx-auto bg-[#2A1E17] text-[#FAF7F2] rounded-[2rem] px-8 py-14 flex flex-col lg:flex-row items-start lg:items-center gap-6 justify-between">
          <div>
            <h3 className="font-serif-display text-3xl sm:text-4xl">
              Hungry yet? <em className="italic text-[#D4A373]">Reserve a corner.</em>
            </h3>
            <p className="text-sm text-[#FAF7F2]/70 mt-3 max-w-lg">
              Pick a café, pick a corner, plan your evening. We'll keep the seat warm.
            </p>
          </div>
          <a href="/contact" className="btn-primary bg-[#C76D4B] hover:bg-[#A55639]" data-testid="menu-cta-reserve">
            Reserve a Corner
          </a>
        </div>
      </section>
    </div>
  );
}
